import React, { lazy, Suspense, useState } from 'react'
import { css } from '@linaria/core'
import { colors, LayoutBand, Row, AtContainerWidth } from '@fs/zion-ui'
import { ResponsiveProvider } from '@fs/zion-ui/quarks-authorized-use-only'
import MinNavLinks from './NavLinks'
import MinLegalText from './LegalText'
import FooterExpansion from './FooterExpansion'
import FooterExpansionButton from './FooterExpansionButton'

const MinChurchLogo = lazy(() => import('./ChurchLogo'))

const accentColor = colors.gray02

const minFooterCss = css`
  font-size: 14px;
  background-color: ${colors.gray00};
  border-top: 10px solid ${accentColor};
`

const mobileLegalCss = css`
  text-align: center;
`

// keep the legal band at 80px tall even when in non-religious mode and the logo is gone
const legalBandCss = css`
  min-height: 80px;
`

const atWidthConfig = { default: 'small', md: 'mid', xl: 'wide' }

export default function MinFooter({
  menaReligiousMode = false,
  nonReligiousMode = true,
  canSeeTemple = false,
  showArabicExperience,
  external = false,
  hideLinksBand = false,
  ...props
}) {
  const [open, setOpen] = useState(false)

  const logo = !menaReligiousMode && !nonReligiousMode && !showArabicExperience && (
    <Suspense fallback="">
      <MinChurchLogo />
    </Suspense>
  )

  const legalText = (
    <MinLegalText
      nonReligiousMode={nonReligiousMode}
      menaReligiousMode={menaReligiousMode}
      showArabicExperience={showArabicExperience}
      external={external}
    />
  )

  return (
    <footer className={minFooterCss} role="contentinfo" {...props}>
      {/* ResponsiveProvider is here because we aren't wrapped in a responsive parent item, just `footer` */}
      {/* We need this so the footer responds to its container, not the page, in case the footer is affected by a side panel */}
      <ResponsiveProvider>
        <AtContainerWidth>
          {(atWidth) => (
            <>
              {!hideLinksBand && (
                <LayoutBand marginY="xs" maxWidth="md">
                  {!open ? (
                    <MinNavLinks
                      showArabicExperience={showArabicExperience}
                      nonReligiousMode={nonReligiousMode}
                      viewWidth={atWidth(atWidthConfig)}
                      external={external}
                      handleExpandClick={() => setOpen(!open)}
                    />
                  ) : (
                    <FooterExpansionButton onClick={() => setOpen(!open)} />
                  )}
                  <FooterExpansion
                    nonReligiousMode={nonReligiousMode}
                    canSeeTemple={canSeeTemple}
                    expanderConfig={{ open, setOpen }}
                    external={external}
                  />
                </LayoutBand>
              )}
              <LayoutBand
                color={accentColor}
                marginY={atWidth(atWidthConfig) === 'small' ? 'sm' : 'none'}
                maxWidth="md"
              >
                {atWidth(atWidthConfig) === 'small' ? (
                  <Row alignX="center" wrap>
                    {logo}
                    <div className={mobileLegalCss}>{legalText}</div>
                  </Row>
                ) : (
                  <Row alignY="middle" className={legalBandCss}>
                    <div growX>{legalText}</div>
                    <div noShrinkX>{logo}</div>
                  </Row>
                )}
              </LayoutBand>
            </>
          )}
        </AtContainerWidth>
      </ResponsiveProvider>
    </footer>
  )
}
